import type { ContactMessage, ContactState } from './contact-selectors';
import { scheduledMessagesForContactIdSelector } from './contact-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';

import {
  Action,
  updateStatusById,
  setStatusDirtyById,
  updateResourceByParam,
  setStateDirtyByParam,
} from './utils/state-manager';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: ContactState = {
  messagesByContactId: {},
  scheduledMessagesById: {},
  scheduledMessagesStateByContactId: {},
  callsByContactId: {},
  callsByContactIdStatus: {},
};

const updateContactMessages = (
  state: ContactState,
  contactId: number,
  update: Record<string, any>,
) => {
  const messagesByContactId = { ...state.messagesByContactId };
  messagesByContactId[contactId] = { ...messagesByContactId[contactId], ...update };
  return { ...state, messagesByContactId };
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_CONTACT_MESSAGES'): {
      const { contactId } = action.payload.args;
      return updateContactMessages(state, contactId, {
        loading: true,
      });
    }
    case SUCCESS('LOAD_CONTACT_MESSAGES'): {
      const { args, data } = action.payload;
      return updateContactMessages(state, args.contactId, {
        dirty: false,
        loading: false,
        messages: data.messages,
        scheduledMessages: data.scheduledMessages,
      });
    }
    case SUCCESS('DELETE_EMPLOYEE'): {
      const { contactId } = action.payload.args;
      const newState = state;
      // these messages are for employee message and the only other
      // thing that might go in here would be referrals
      // job applicant messages are in application activity
      delete newState.messagesByContactId[contactId];
      return newState;
    }
    case STARTING('LOAD_CONTACT_CALLS'): {
      const { args } = action.payload;
      return updateStatusById(state, 'callsByContactId', true, args.contactId);
    }
    case SUCCESS('LOAD_CONTACT_CALLS'): {
      const { args, data } = action.payload;
      return updateStatusById(state, 'callsByContactId', false, args.contactId, data);
    }
    case FAILURE('LOAD_CONTACT_CALLS'): {
      const { args } = action.payload;
      return updateStatusById(state, 'callsByContactId', false, args.contactId);
    }
    case SUCCESS('MAKE_CALL'): {
      const { contactId } = action.payload.args;
      if (contactId) {
        return setStatusDirtyById(state, 'callsByContactId', contactId);
      }
      return state;
    }
    case SUCCESS('SEND_TEXT_MESSAGE'): {
      const { contactId } = action.payload;
      return updateContactMessages(state, contactId, {
        dirty: true,
      });
    }
    case SUCCESS('SEND_BULK_MESSAGE'): {
      const { data } = action.payload;
      let newState = { ...state };
      data.forEach((m: ScheduledMessage) => {
        newState = updateContactMessages(newState, m.contactId, {
          dirty: true,
        });
      });
      return newState;
    }
    case SUCCESS('UPDATE_SCHEDULED_MESSAGE'):
    case FAILURE('UPDATE_SCHEDULED_MESSAGE'):
    case SUCCESS('CANCEL_SCHEDULED_MESSAGE'):
    case 'REFRESH_MESSAGES': {
      const { contactId } = action.payload.args;
      const newState = updateContactMessages(state, contactId, {
        dirty: true,
      });
      return setStateDirtyByParam(newState, 'scheduledMessages', 'contactId', contactId);
    }
    case 'SET_CONTACT_MESSAGES_DIRTY': {
      const { contactId } = action.payload;
      return updateContactMessages(state, contactId, { dirty: true });
    }
    case 'SET_DIRTY_ON_TAB_REGAIN_VISIBILITY': {
      let newState = { ...state };
      const contactMessages = newState.messagesByContactId;
      Object.keys(contactMessages).forEach((contactId) => {
        newState = updateContactMessages(newState, parseInt(contactId, 10), { dirty: true });
      });
      return newState;
    }
    case SUCCESS('CREATE_ANNOUNCEMENT'): {
      // We don't have enough information on the client side to know who will receive
      // this announcement (ie: we can't map contacts to employee groups here)
      // So we just clear out the messages we have loaded for everyone
      state.messagesByContactId = {};
      return state;
    }
    case SUCCESS('UPDATE_ANNOUNCEMENT'): {
      const { announcementId } = action.payload.args;
      const contactsWithAnnouncement: Array<number> = [];
      Object.values<ContactMessage>(state.messagesByContactId).forEach((contactMessage) => {
        if (!contactMessage.scheduledMessages) {
          // If we've never loaded the scheduled messages for this employee, then there's nothing to do
          return;
        }
        contactMessage.scheduledMessages.forEach((scheduledMessage) => {
          if (scheduledMessage.Announcement?.announcementId === announcementId) {
            contactsWithAnnouncement.push(scheduledMessage.contactId);
          }
        });
      });
      let newState = state;
      contactsWithAnnouncement.forEach((contactId) => {
        newState = updateContactMessages(newState, contactId, { dirty: true });
      });
      return newState;
    }
    case STARTING('LOAD_CONTACT_SCHEDULED_MESSAGES'): {
      const { contactId } = action.payload.args;
      return updateResourceByParam(state, 'scheduledMessages', 'contactId', contactId, true);
    }
    case SUCCESS('LOAD_CONTACT_SCHEDULED_MESSAGES'): {
      const {
        args: { contactId },
        data,
      } = action.payload;
      // We have to remove the scheduled messages that currently in the state for this contactId. The main
      // concern is for when you move statuses - we cancel some messages, so it's possible that the data loaded has
      // removed some of the scheduled messages
      const scheduledMessagesForContact = scheduledMessagesForContactIdSelector(
        { contacts: state },
        contactId,
      );
      scheduledMessagesForContact.forEach((sm) => {
        delete state.scheduledMessagesById[sm.scheduledMessageId];
      });

      return updateResourceByParam(
        state,
        'scheduledMessages',
        'contactId',
        contactId,
        false,
        convertArrayToMap(data, (obj: ScheduledMessage) => String(obj.scheduledMessageId)),
      );
    }
    case FAILURE('LOAD_CONTACT_SCHEDULED_MESSAGES'): {
      const { contactId } = action.payload.args;
      return updateResourceByParam(state, 'scheduledMessages', 'contactId', contactId, false);
    }
    default:
      return state;
  }
};
